import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { storeService, Product, Order, CartItem } from '../services/storeService';

interface StoreContextType {
  products: Product[];
  loading: boolean;
  error: string | null;
  cart: CartItem[];
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateCartItemQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: number;
  getTotalPrice: number;
  isInCart: (productId: string) => boolean;
  getCartItemQuantity: (productId: string) => number;
  createOrder: (orderData: Omit<Order, 'id' | 'created_at' | 'updated_at'>) => Promise<Order>;
  getProductById: (id: string) => Promise<Product | null>;
  getProductsByCategory: (category: string) => Promise<Product[]>;
  refreshProducts: () => Promise<void>;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const useStore = () => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};

interface StoreProviderProps {
  children: React.ReactNode;
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);

  // Load products from Firestore
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await storeService.getProducts();
      setProducts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load products on mount
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('clever-details-cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        // Validate cart structure before setting
        if (Array.isArray(parsedCart)) {
          setCart(parsedCart);
        } else {
          console.warn('Invalid cart structure, clearing cart');
          localStorage.removeItem('clever-details-cart');
        }
      } catch (err) {
        console.error('Error parsing saved cart:', err);
        localStorage.removeItem('clever-details-cart');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('clever-details-cart', JSON.stringify(cart));
  }, [cart]);

  // Cart operations
  const addToCart = useCallback((product: Product, quantity: number = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      
      if (existingItem) {
        return prevCart.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        return [...prevCart, { product, quantity }];
      }
    });
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  }, []);

  const updateCartItemQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  // Computed values
  const getTotalItems = useMemo(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  const getTotalPrice = useMemo(() => {
    return cart.reduce((total, item) => {
      return total + (item.product.price * item.quantity);
    }, 0);
  }, [cart]);

  const isInCart = useCallback((productId: string) => {
    return cart.some(item => item.product.id === productId);
  }, [cart]);

  const getCartItemQuantity = useCallback((productId: string) => {
    const item = cart.find(item => item.product.id === productId);
    return item ? item.quantity : 0;
  }, [cart]);

  // Order operations
  const createOrder = useCallback(async (orderData: Omit<Order, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const order = await storeService.createOrder(orderData);
      // Clear cart after successful order
      clearCart();
      return order;
    } catch (err) {
      console.error('Error creating order:', err);
      throw err;
    }
  }, [clearCart]);

  // Product operations
  const getProductById = useCallback(async (id: string) => {
    try {
      return await storeService.getProductById(id);
    } catch (err) {
      console.error('Error fetching product:', err);
      return null;
    }
  }, []);

  const getProductsByCategory = useCallback(async (category: string) => {
    try {
      return await storeService.getProductsByCategory(category);
    } catch (err) {
      console.error('Error fetching products by category:', err);
      return [];
    }
  }, []);

  const refreshProducts = useCallback(async () => {
    await loadProducts();
  }, [loadProducts]);

  const value: StoreContextType = {
    products,
    loading,
    error,
    cart,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    isInCart,
    getCartItemQuantity,
    createOrder,
    getProductById,
    getProductsByCategory,
    refreshProducts,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};
